<template>
  <div class="landing">
    <BlogNav />

    <!-- HERO -->
    <section class="hero" ref="hero">
      <div class="hero-inner">
        <h1 class="hero-title" data-reveal>
          <span class="hero-title-line">{{ $t('landing.about.hero.titleLine1') }}</span>
          <span class="hero-title-line accent">{{ $t('landing.about.hero.titleLine2') }}</span>
        </h1>

        <p class="hero-sub" data-reveal>
          {{ $t('landing.about.hero.subtitle') }}
          <strong>{{ $t('landing.about.hero.subtitleStrong') }}</strong>
        </p>

        <div class="hero-cta-row" data-reveal>
          <a
            class="hero-btn hero-btn--primary"
            href="https://calendly.com/d/cytz-wbs-3xn/demo-query-juriste-ia-recherche-juridique"
            @click.prevent="openCalendlyDemo"
            target="_blank"
            rel="noopener"
          >
            {{ $t('landing.about.hero.ctaDemo') }}
          </a>
        </div>

        <div class="hero-proof" data-reveal>
          <span class="hero-proof-label">{{ $t('landing.about.hero.socialProof') }}</span>
          <div class="hero-proof-logos">
            <template v-for="logo in heroLogos">
              <img
                v-if="logo.src"
                :key="logo.name"
                class="hero-proof-logo"
                :src="logo.src"
                :alt="`${$t('landing.about.trust.logoAltPrefix')} ${logo.name}`"
              />
              <span v-else :key="logo.name" class="hero-proof-logo hero-proof-logo--text">
                {{ logo.name }}
              </span>
            </template>
          </div>
        </div>
      </div>

      <!-- Capture du produit + deux cartes flottantes (maquette Figma « Hero ») -->
      <div class="hero-showcase" data-reveal>
        <div class="hero-showcase-frame">
          <img
            class="hero-showcase-img"
            src="@/assets/hero-v2/app-screenshot.png"
            :alt="$t('landing.about.hero.imageAlt')"
          />
        </div>
        <img
          class="hero-card hero-card--integrations"
          src="@/assets/hero-v2/card-integrations.png"
          alt=""
          aria-hidden="true"
        />
        <img
          class="hero-card hero-card--sources"
          src="@/assets/hero-v2/card-sources.png"
          alt=""
          aria-hidden="true"
        />
      </div>
      <div class="hero-fade" aria-hidden="true"></div>
    </section>

    <!-- TRUST -->
    <section class="trust" ref="trust">
      <h3 class="trust-title" data-reveal>{{ $t('landing.about.trust.title') }}</h3>
      <p class="trust-note" data-reveal>{{ $t('landing.about.trust.note') }}</p>

      <div class="trust-stats" ref="trustStats">
        <div class="trust-stat" data-reveal>
          <span class="trust-stat-accent"></span>
          <span class="trust-stat-number" data-counter data-target="500000" data-format="thousands" data-suffix="+">0</span>
          <span class="trust-stat-label">{{ $t('landing.about.trust.stats.queries') }}</span>
        </div>
        <div class="trust-stat" data-reveal>
          <span class="trust-stat-accent"></span>
          <span class="trust-stat-number" data-counter data-target="250" data-suffix="+">0</span>
          <span class="trust-stat-label">{{ $t('landing.about.trust.stats.firms') }}</span>
        </div>
        <div class="trust-stat" data-reveal>
          <span class="trust-stat-accent"></span>
          <span class="trust-stat-number" data-counter data-target="15" data-suffix="+">0</span>
          <span class="trust-stat-label">{{ $t('landing.about.trust.stats.sources') }}</span>
        </div>
      </div>

      <a
        class="trust-cta"
        href="https://calendly.com/d/cytz-wbs-3xn/demo-query-juriste-ia-recherche-juridique"
        @click.prevent="openCalendlyDemo"
        data-reveal
      >
        {{ $t('landing.about.trust.cta') }}
      </a>
    </section>

    <!-- EUROPE / SOUVERAINETÉ -->
    <section class="europe-section">
      <div class="europe-inner">
        <span class="europe-badge" data-reveal>{{ $t('landing.about.europe.badge') }}</span>
        <h2 class="europe-title" data-reveal>{{ $t('landing.about.europe.title') }}</h2>
        <p class="europe-sub" data-reveal>{{ $t('landing.about.europe.subtitle') }}</p>

        <div class="europe-grid">
          <div class="europe-card" data-reveal>
            <img class="europe-card-icon" src="@/assets/hero-v2/icon-shield.svg" alt="" width="28" height="28" />
            <h4>{{ $t('landing.about.europe.encryptionTitle') }}</h4>
            <p>{{ $t('landing.about.europe.encryptionText') }}</p>
          </div>
          <div class="europe-card" data-reveal>
            <img class="europe-card-icon" src="@/assets/hero-v2/icon-shield.svg" alt="" width="28" height="28" />
            <h4>{{ $t('landing.about.europe.hostingTitle') }}</h4>
            <p>{{ $t('landing.about.europe.hostingText') }}</p>
          </div>
          <div class="europe-card" data-reveal>
            <img class="europe-card-icon" src="@/assets/hero-v2/icon-shield.svg" alt="" width="28" height="28" />
            <h4>{{ $t('landing.about.europe.gdprTitle') }}</h4>
            <p>{{ $t('landing.about.europe.gdprText') }}</p>
          </div>
          <div class="europe-card" data-reveal>
            <img class="europe-card-icon" src="@/assets/hero-v2/icon-shield.svg" alt="" width="28" height="28" />
            <h4>{{ $t('landing.about.europe.isolationTitle') }}</h4>
            <p>{{ $t('landing.about.europe.isolationText') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- FEATURE TWO-COL -->
    <section class="feature-two-col">
      <div class="col text">
        <a href="#" class="link-info" data-reveal>{{ $t('landing.about.feature.linkInfo') }}</a>
        <h2 class="section-heading" data-reveal>{{ $t('landing.about.feature.heading') }}</h2>
        <p class="section-paragraph" data-reveal>{{ $t('landing.about.feature.paragraph') }}</p>

        <ul class="checklist">
          <li data-reveal>
            <img class="check" src="@/assets/hero-v2/icon-check.svg" alt="" />
            {{ $t('landing.about.feature.check1') }}
          </li>
          <li data-reveal>
            <img class="check" src="@/assets/hero-v2/icon-check.svg" alt="" />
            {{ $t('landing.about.feature.check2') }}
          </li>
          <li data-reveal>
            <img class="check" src="@/assets/hero-v2/icon-check.svg" alt="" />
            {{ $t('landing.about.feature.check3') }}
          </li>
        </ul>
      </div>

      <div class="col visual" data-reveal>
        <div class="visual-frame">
          <img
            src="@/assets/hero-v2/prompt-box-mini.png"
            class="main-visual"
            :alt="$t('landing.about.feature.imageAlt')"
          />
        </div>
      </div>
    </section>

    <!-- CTA BAND (maquette Figma « CTA card ») -->
    <section class="cta-band">
      <h3 class="cta-title" data-reveal>{{ $t('landing.about.cta.title') }}</h3>
      <p class="cta-text" data-reveal>{{ $t('landing.about.cta.text') }}</p>
      <a
        class="cta-btn"
        href="https://calendly.com/d/cytz-wbs-3xn/demo-query-juriste-ia-recherche-juridique"
        @click.prevent="openCalendlyDemo"
        data-reveal
      >{{ $t('landing.about.cta.button') }}</a>
      <p class="cta-foot" data-reveal>{{ $t('landing.about.cta.foot') }}</p>
    </section>

    <!-- FOOTER -->
    <footer class="footer">
      <div class="footer-content">
        <div class="footer-logo-section">
          <img src="@/assets/hero-v2/logo-query-lexroom.svg" :alt="$t('landing.about.footer.logoAlt')" class="logo" />
          <p class="footer-description">{{ $t('landing.about.footer.description') }}</p>
        </div>

        <div class="footer-links-grid">
          <div class="footer-column">
            <h4>{{ $t('landing.about.footer.columnPages') }}</h4>
            <ul>
              <li><a href="/">{{ $t('landing.about.footer.linkHome') }}</a></li>
              <li><a href="https://www.query-juriste.com/blog">{{ $t('landing.about.footer.linkBlog') }}</a></li>
              <li><a href="/ia">{{ $t('landing.about.footer.linkIa') }}</a></li>
              <li><a href="/sources-jurisprudentielles">{{ $t('landing.about.footer.linkSources') }}</a></li>
              <li><a href="/faq">{{ $t('landing.about.footer.linkFaq') }}</a></li>
            </ul>
          </div>

          <div class="footer-column">
            <h4>{{ $t('landing.about.footer.columnProduct') }}</h4>
            <ul>
              <li><a href="/loginPage">{{ $t('landing.about.footer.linkLogin') }}</a></li>
              <li><a href="/contactPage">{{ $t('landing.about.footer.linkContact') }}</a></li>
            </ul>
          </div>

          <div class="footer-column">
            <h4>{{ $t('landing.about.footer.columnCompany') }}</h4>
            <ul>
              <li><a href="/mentionsPage">{{ $t('landing.about.footer.linkMentions') }}</a></li>
              <li><a href="/conditions-utilisation">{{ $t('landing.about.footer.linkCgu') }}</a></li>
              <li><a href="/conditions-vente">{{ $t('landing.about.footer.linkCgv') }}</a></li>
              <li><a href="/politique-donnees">{{ $t('landing.about.footer.linkPrivacy') }}</a></li>
            </ul>
          </div>
        </div>
      </div>

      <div class="footer-bottom">
        <p>{{ $t('landing.about.footer.copyright') }}</p>
      </div>
    </footer>
  </div>
</template>

<script>
import BlogNav from "@/components/BlogNav.vue";
import { openCalendlyDemo } from "@/utils/calendly";
import logoVeilJourde from "@/assets/landing-lexroom/logo-veil-jourde.png";
import logoBg2v from "@/assets/landing-lexroom/logo-bg2v.png";
import logoDePardieu from "@/assets/landing-lexroom/logo-de-pardieu.png";
import logoBredinPrat from "@/assets/landing-lexroom/logo-bredin-prat.png";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default {
  name: "RootPage",
  components: { BlogNav },
  data() {
    return {
      section4: [
        { img: "card1", icon: "icon1", titleKey: "landing.about.cards.card1Title", textKey: "landing.about.cards.card1Text" },
        { img: "card2", icon: "icon2", titleKey: "landing.about.cards.card2Title", textKey: "landing.about.cards.card2Text" },
        { img: "card3", icon: "icon3", titleKey: "landing.about.cards.card3Title", textKey: "landing.about.cards.card3Text" },
      ],
      // Ordre et rendu de la maquette Figma : 4 logos image, Gibson Dunn et Jeantet en texte
      heroLogos: [
        { name: "Veil Jourde", src: logoVeilJourde },
        { name: "BG2V", src: logoBg2v },
        { name: "De Pardieu Brocas Maffei", src: logoDePardieu },
        { name: "Bredin Prat", src: logoBredinPrat },
        { name: "GIBSON DUNN" },
        { name: "JEANTET" },
      ],
    };
  },
  mounted() {
    this.$nextTick(() => this.initAnimations());
    // ?demo=1 : ouvre directement le formulaire de demo. Sert aux anciens
    // liens d'essai gratuit (/signinPage, blog, Google Ads) redirigés ici.
    if (this.$route.query.demo) this.openCalendlyDemo();
  },
  beforeDestroy() {
    if (this.heroTimeline) this.heroTimeline.kill();
    ScrollTrigger.getAll().forEach(st => st.kill());
  },
  methods: {
    openCalendlyDemo,

    initAnimations() {
      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReduced) {
        document.querySelectorAll('[data-reveal]').forEach(el => {
          el.style.opacity = 1;
          el.style.transform = 'none';
        });
        return;
      }

      const heroEls = this.$refs.hero ? this.$refs.hero.querySelectorAll('[data-reveal]') : [];
      if (heroEls.length) {
        this.heroTimeline = gsap.timeline({ defaults: { ease: 'power3.out', duration: 0.9 } });
        this.heroTimeline.from(heroEls, {
          opacity: 0,
          y: 28,
          stagger: 0.12,
        });
      }

      const otherReveals = document.querySelectorAll('section:not(.hero) [data-reveal]');
      otherReveals.forEach(el => {
        gsap.from(el, {
          opacity: 0,
          y: 32,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 85%',
            toggleActions: 'play none none none',
          },
        });
      });

      const counters = document.querySelectorAll('[data-counter]');
      counters.forEach(el => {
        const target = parseInt(el.dataset.target, 10);
        const suffix = el.dataset.suffix || '';
        const useThousands = el.dataset.format === 'thousands';
        const obj = { value: 0 };
        ScrollTrigger.create({
          trigger: el,
          start: 'top 85%',
          once: true,
          onEnter: () => {
            gsap.to(obj, {
              value: target,
              duration: 1.8,
              ease: 'power2.out',
              onUpdate: () => {
                const v = Math.round(obj.value);
                el.textContent = useThousands
                  ? v.toLocaleString('fr-FR') + suffix
                  : v + suffix;
              },
            });
          },
        });
      });
    },
  },
  metaInfo() {
    return {
      title: this.$t('landing.about.metaTitle'),
      meta: [{ name: "description", content: this.$t('landing.about.metaDescription') }],
      link: [{ rel: "canonical", href: "https://www.query-juriste.com/" }],
    };
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Inter:wght@400;500;600;700&family=Manrope:wght@400;500;600;700;800&family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,500;0,6..72,600;1,6..72,400;1,6..72,500&display=swap');
/* ============================================================
   DESIGN TOKENS — Hybride : base épurée + violet brand accent
   ============================================================ */
.landing {
  --bg: #ffffff;
  --bg-elev: #f8fafc;
  --text: #0f172a;
  --text-muted: #475569;
  --text-subtle: #64748b;
  --border: #e2e8f0;
  --border-soft: #f1f5f9;

  --brand: #625cfb;
  --brand-soft: #eeedfd;
  --brand-dark: #5046e3;

  --navy: #1e3a8a;
  --navy-deep: #120d31;

  --gold: #b45309;
  --gold-soft: #fef3c7;

  --shadow-sm: 0 2px 8px rgba(15, 23, 42, 0.04);
  --shadow-md: 0 12px 32px rgba(15, 23, 42, 0.08);
  --shadow-lg: 0 28px 64px rgba(15, 23, 42, 0.14);
  --shadow-brand: 0 24px 64px rgba(98, 92, 251, 0.28);

  --radius: 14px;
  --radius-lg: 22px;
  --radius-pill: 999px;

  --font-display: 'Manrope', ui-sans-serif, system-ui, sans-serif;
  --font-sans: 'Manrope', ui-sans-serif, system-ui, sans-serif;
  --font-serif: 'Newsreader', 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif;

  background: var(--bg);
  color: var(--text);
  font-family: var(--font-sans);
  -webkit-font-smoothing: antialiased;
  /* Rendu plus compact/aéré, équivalent d'un zoom navigateur à 90 % */
  zoom: 0.9;
}

/* ============================================================
   BUTTONS
   ============================================================ */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 50px;
  padding: 0 26px;
  font-family: var(--font-sans);
  font-weight: 600;
  font-size: 15px;
  letter-spacing: -0.01em;
  text-decoration: none;
  border-radius: var(--radius-pill);
  border: 1.5px solid transparent;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.3s ease, background 0.25s ease, border-color 0.25s ease, color 0.25s ease;
  will-change: transform;
}

.btn-primary {
  background: var(--brand);
  color: #fff;
  box-shadow: var(--shadow-brand);
}
.btn-primary:hover {
  background: var(--brand-dark);
  transform: translateY(-2px);
  box-shadow: 0 32px 72px rgba(98, 92, 251, 0.34);
}
.btn-primary .btn-arrow { transition: transform 0.25s ease; }
.btn-primary:hover .btn-arrow { transform: translateX(4px); }

.btn-secondary {
  background: transparent;
  color: var(--text);
  border-color: var(--border);
}
.btn-secondary:hover {
  border-color: var(--text);
  transform: translateY(-2px);
}

.btn-outline {
  background: transparent;
  border-color: var(--border);
  color: var(--text);
}
.btn-outline:hover {
  border-color: var(--brand);
  color: var(--brand);
}

.btn-ghost {
  background: #fff;
  color: var(--brand);
  border-color: #fff;
}
.btn-ghost:hover {
  background: rgba(255, 255, 255, 0.92);
  transform: translateY(-1px);
}

/* ============================================================
   HERO (maquette Figma « Hero », node 2622:46)
   ============================================================ */
.hero {
  --hero-blue: #1c73e7;
  --hero-blue-300: #7ea3fb;
  --hero-ink: #1a1a1c;
  --hero-gray: #545458;
  --hero-font: 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  --hero-serif: 'DM Serif Display', 'Newsreader', Georgia, serif;

  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  padding: 140px 20px 0;
  overflow: hidden;
  isolation: isolate;
  background: linear-gradient(180deg, #f7fbff 40%, #0f4c9d 100%);
}

.hero-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  max-width: 1200px;
  text-align: center;
}

.hero-title {
  display: flex;
  flex-direction: column;
  margin: 0;
  font-family: var(--hero-serif);
  font-weight: 400;
  font-size: clamp(44px, 6.4vw, 80px);
  line-height: 1;
  color: var(--hero-ink);
}
.hero-title .accent {
  font-style: italic;
  color: var(--hero-blue);
  -webkit-text-fill-color: var(--hero-blue);
}

.hero-sub {
  margin: 0;
  font-family: var(--hero-font);
  font-size: 20px;
  line-height: 1.25;
  color: var(--hero-gray);
}
.hero-sub strong {
  font-weight: 600;
  color: var(--hero-ink);
}

.hero-cta-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
}

.hero-btn {
  display: inline-flex;
  align-items: center;
  padding: 12px 22px;
  border: 1px solid transparent;
  border-radius: 99px;
  font-family: var(--hero-font);
  font-weight: 600;
  font-size: 14px;
  line-height: normal;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}
.hero-btn:hover { transform: translateY(-1px); }
.hero-btn--primary {
  background: var(--hero-blue);
  color: #fff;
}
.hero-btn--primary:hover {
  background: #1562c9;
  color: #fff;
}
.hero-btn--secondary {
  background: #fff;
  border-color: var(--hero-blue-300);
  color: var(--hero-blue);
}
.hero-btn--secondary:hover {
  border-color: var(--hero-blue);
  color: var(--hero-blue);
}

/* Social proof : cabinets clients, sous les CTA */
.hero-proof {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}
.hero-proof-label {
  font-family: var(--hero-font);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.96px;
  text-transform: uppercase;
  color: var(--hero-gray);
}
.hero-proof-logos {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 20px;
}
.hero-proof-logo {
  width: 116px;
  height: 46px;
  object-fit: contain;
}
.hero-proof-logo--text {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: var(--hero-font);
  font-weight: 700;
  font-size: 15px;
  letter-spacing: 0.9px;
  color: var(--hero-ink);
  white-space: nowrap;
}

/* Capture produit + cartes flottantes */
.hero-showcase {
  position: relative;
  width: 100%;
  max-width: 1280px;
  height: 622px;
}
.hero-showcase-frame {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: min(1000px, 100%);
  height: 100%;
  padding: 8px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 4px;
  overflow: hidden;
}
.hero-showcase-img {
  display: block;
  width: 100%;
  aspect-ratio: 994 / 614;
  object-fit: cover;
  object-position: top;
  border-radius: 2px;
  box-shadow: 0 8px 20px rgba(3, 30, 70, 0.3);
}
.hero-card {
  position: absolute;
  display: block;
  height: auto;
}
.hero-card--integrations {
  top: 139px;
  right: 27px;
  width: 196px;
  height: 267px;
  object-fit: contain;
}
.hero-card--sources {
  top: 220px;
  left: 0;
  width: 321px;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
}

.hero-fade {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 180px;
  background: linear-gradient(0deg, #fff 0%, rgba(255, 255, 255, 0) 100%);
  pointer-events: none;
  z-index: 1;
}

@media (max-width: 1100px) {
  .hero-card--integrations { right: 0; width: 160px; height: 218px; }
  .hero-card--sources { width: 260px; }
}

@media (max-width: 768px) {
  .hero { gap: 32px; }
  .hero-inner { gap: 28px; }
  .hero-sub { font-size: 17px; }
  .hero-proof-logos { gap: 8px 16px; }
  .hero-proof-logo { width: 100px; height: 40px; }
  .hero-showcase { height: auto; }
  .hero-showcase-frame {
    position: relative;
    left: auto;
    transform: none;
    height: auto;
  }
  .hero-card { display: none; }
  .hero-fade { height: 120px; }
}

/* ============================================================
   TRUST (maquette Figma « Ils nous font confiance »)
   ============================================================ */
.trust {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  max-width: 1440px;
  margin: 0 auto;
  padding: 96px 120px;
  text-align: center;
  font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
}
.trust-title {
  margin: 0;
  font-family: inherit;
  font-weight: 600;
  font-size: clamp(30px, 3.6vw, 40px);
  line-height: 1.2;
  color: #1a1a1c;
}
.trust-note {
  margin: 0;
  max-width: 820px;
  font-size: 18px;
  line-height: 1.4;
  color: #545458;
}
.trust-stats {
  display: flex;
  gap: 24px;
  width: 100%;
  padding: 24px 0;
}
.trust-stat {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding: 32px 24px;
  border: 1px solid #e4eaf3;
  border-radius: 8px;
  background: #f7fbff;
}
/* Trait bleu au-dessus du chiffre */
.trust-stat-accent {
  display: block;
  width: 32px;
  height: 2px;
  margin: 9px 0 12px;
  background: #155fc1;
}
.trust-stat-number {
  font-family: 'DM Serif Display', Georgia, serif;
  font-weight: 400;
  font-size: clamp(44px, 5vw, 60px);
  line-height: 1.2;
  color: #01102b;
}
.trust-stat-label {
  font-size: 16px;
  color: #1a1a1c;
}
.trust-cta {
  display: inline-flex;
  align-items: center;
  padding: 12px 22px;
  border-radius: 99px;
  background: #1c73e7;
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  text-decoration: none;
  transition: background-color .2s ease, transform .2s ease;
}
.trust-cta:hover { background: #1562c9; color: #fff; transform: translateY(-1px); }

/* ============================================================
   EUROPE / SÉCURITÉ (maquette Figma « Souveraineté numérique »)
   ============================================================ */
.europe-section {
  background: #031e46;
  color: #fff;
  padding: 96px 120px;
  font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
}
.europe-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
}
.europe-badge {
  display: inline-block;
  padding: 6px 14px;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.1);
  color: #d2dcfe;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.96px;
  text-transform: uppercase;
}
.europe-title {
  margin: 0;
  font-family: inherit;
  font-weight: 600;
  font-size: clamp(30px, 4vw, 44px);
  line-height: 1.2;
  color: #fff;
}
.europe-sub {
  margin: 0;
  max-width: 760px;
  font-size: 18px;
  line-height: 1.5;
  color: #c4c2ff;
}
.europe-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  width: 100%;
  padding-top: 32px;
  text-align: left;
}
.europe-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 28px 24px;
  border-radius: 16px;
  background: #fff;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.europe-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.25);
}
.europe-card-icon {
  display: block;
  width: 28px;
  height: 28px;
}
.europe-card h4 {
  margin: 0;
  font-family: inherit;
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1c;
}
.europe-card p {
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: #545458;
}

/* ============================================================
   FEATURE TWO-COL (maquette Figma « Raisonne en juriste »)
   ============================================================ */
.feature-two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
  max-width: 1440px;
  margin: 0 auto;
  padding: 112px 120px;
  font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
}
.feature-two-col .col.text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  min-width: 0;
}
.link-info {
  display: inline-block;
  color: #1c73e7;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
}
.link-info::after { content: " \2192"; }
.link-info:hover { text-decoration: underline; }
.section-heading {
  font-family: inherit;
  font-weight: 600;
  margin: 0;
  font-size: clamp(30px, 4vw, 44px);
  line-height: 1.15;
  color: #1a1a1c;
}
.section-paragraph {
  margin: 0;
  color: #545458;
  font-size: 18px;
  line-height: 1.5;
}
.checklist {
  display: flex;
  flex-direction: column;
  gap: 14px;
  list-style: none;
  margin: 0;
  padding: 8px 0 0;
}
.checklist li {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #1a1a1c;
  font-size: 17px;
}
.checklist .check { width: 22px; height: 22px; flex-shrink: 0; }

.feature-two-col .col.visual { min-width: 0; }
.visual-frame {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 400px;
  padding: 22px 12px;
  border-radius: 8px;
  background: linear-gradient(180deg, #d2dcfe 0%, #7ea3fb 100%);
}
.main-visual {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* ============================================================
   CARDS
   ============================================================ */
.cards-section {
  background: var(--bg-elev);
  padding: 100px 20px;
}
.big-heading {
  font-family: var(--font-display);
  font-weight: 600;
  max-width: 1200px;
  margin: 0 auto 56px;
  font-size: clamp(30px, 5vw, 56px);
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: var(--text);
  text-align: center;
}
.card-grid {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
}
.info-card {
  position: relative;
  background: #fff;
  border: 1px solid var(--border-soft);
  border-radius: var(--radius-lg);
  padding: 28px 24px 24px;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  cursor: default;
}
.info-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 24px;
  right: 24px;
  height: 3px;
  background: var(--brand);
  border-radius: 0 0 3px 3px;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.4s ease;
}
.info-card:hover {
  transform: translateY(-6px);
  box-shadow: var(--shadow-md);
  border-color: var(--brand-soft);
}
.info-card:hover::before { transform: scaleX(1); }

.card-index {
  position: absolute;
  top: 22px;
  right: 24px;
  font-family: 'JetBrains Mono', ui-monospace, 'Courier New', monospace;
  font-size: 11px;
  color: var(--text-subtle);
  letter-spacing: 0.08em;
  font-weight: 500;
}
.card-hero {
  width: 100%;
  height: auto;
  border-radius: var(--radius);
  margin-bottom: 18px;
}
.card-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 4px 0 10px;
  font-family: var(--font-sans);
  font-size: 17px;
  font-weight: 600;
  color: var(--text);
  letter-spacing: -0.01em;
}
.card-icon { width: 22px; height: 22px; }
.card-text {
  color: var(--text-muted);
  font-size: 14.5px;
  line-height: 1.6;
  margin: 0;
}

/* ============================================================
   CTA BAND (maquette Figma « CTA card »)
   ============================================================ */
.cta-band {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px 20px;
  background: #0f4c9d;
  color: #fff;
  text-align: center;
  font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
}
.cta-title {
  margin: 0;
  font-family: inherit;
  font-weight: 600;
  font-size: clamp(28px, 4vw, 40px);
  line-height: 1.2;
  color: #fff;
}
.cta-text {
  margin: 0;
  font-size: 18px;
  line-height: 1.4;
  color: #e6e5ff;
}
.cta-btn {
  display: inline-flex;
  align-items: center;
  margin-top: 8px;
  padding: 12px 22px;
  border: 1px solid #7ea3fb;
  border-radius: 99px;
  background: #fff;
  color: #1c73e7;
  font-weight: 600;
  font-size: 14px;
  text-decoration: none;
  transition: transform 0.2s ease, background 0.2s ease;
}
.cta-btn:hover {
  transform: translateY(-1px);
  background: #f7fbff;
  color: #1c73e7;
}
.cta-foot {
  margin: 0;
  font-size: 13px;
  color: #d2d0ff;
}

/* ============================================================
   FOOTER (maquette Figma « Footer »)
   ============================================================ */
.footer {
  width: 100%;
  background: #f7fbff;
  padding: 72px 0;
  font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
}
.footer-content {
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 120px;
  display: flex;
  justify-content: space-between;
  gap: 48px;
  align-items: start;
}
.footer-logo-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 380px;
}
.footer-logo-section .logo {
  width: 101px;
  height: 60px;
}
.footer-description {
  color: #2c2c2f;
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
  max-width: 300px;
}
/* Desktop : logo + 3 colonnes répartis sur toute la largeur */
.footer-links-grid { display: contents; }
.footer-column h4 {
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  color: #0f4c9d;
  margin: 0 0 12px;
}
.footer-column ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.footer-column a {
  color: #2c2c2f;
  text-decoration: none;
  font-size: 14px;
  transition: color 0.2s ease;
}
.footer-column a:hover { color: #1c73e7; }
.footer-bottom {
  max-width: 1440px;
  margin: 48px auto 0;
  padding: 0 120px;
}
.footer-bottom p {
  margin: 0;
  padding-top: 48px;
  border-top: 1px solid #dbdbdc;
  color: #1a1a1c;
  font-size: 13px;
}

/* ============================================================
   RESPONSIVE
   ============================================================ */
@media (max-width: 1100px) {
  .feature-two-col {
    grid-template-columns: 1fr;
    gap: 40px;
    padding: 80px 40px;
  }
  .footer-content,
  .footer-bottom { padding: 0 40px; }
  .footer-logo-section { width: 300px; }
  .europe-section { padding: 80px 40px; }
  .trust { padding: 80px 40px; }
  .europe-grid { grid-template-columns: 1fr 1fr; }
}

@media (max-width: 900px) {
  .card-grid { grid-template-columns: 1fr 1fr; }
}

@media (max-width: 768px) {
  .feature-two-col { padding: 64px 20px; }
  .europe-section { padding: 64px 20px; }
  .trust { padding: 64px 20px; }
  .visual-frame { height: auto; aspect-ratio: 580 / 400; }
  .cta-text { font-size: 16px; }
  .footer { padding: 56px 0; }
  .footer-content {
    display: grid;
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 0 20px;
  }
  .footer-logo-section { width: auto; }
  .footer-links-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
  .footer-bottom { margin-top: 32px; padding: 0 20px; }
  .footer-bottom p { padding-top: 32px; }
}

@media (max-width: 640px) {
  .hero { padding-top: 90px; }
  .card-grid { grid-template-columns: 1fr; }
  .europe-grid { grid-template-columns: 1fr; }
  .trust-stats { flex-direction: column; gap: 16px; }
}

@media (max-width: 480px) {
  .footer-links-grid { grid-template-columns: 1fr; }
}
</style>
